import {Grid} from "@mui/material";
import React, {useCallback, useEffect, useState} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {ClientAddressFields, Department, Municipality, Province} from "../../../../types/client/clientData";
import {removeDuplicatesById} from "../../../../utils/helpers";
import {HttpClientAddressData} from "../../../../http/client/httpClientAddressData";
import {ControlledTextField} from "../../../../components/forms";

interface AddressFormContentDataProps {
    nameBase: string
}

const AddressFormContentData = ({nameBase}: AddressFormContentDataProps) => {

    const { control, getValues, setValue } = useFormContext()
    const [provinces, setProvinces] = useState<Province[] | null>(null);
    const [partidos, setPartidos] = useState<Department[] | null>(null);
    const [filteredPartidos, setFilteredPartidos] = useState<Department[] | null>(null)
    const [localities, setLocalities] = useState<Municipality[] | null>(null);
    const [filteredlocalities, setFilteredLocalities] = useState<Municipality[] | null>(null);
    const watchPais = useWatch({control, name: `${nameBase}.${ClientAddressFields.CountryCode}`});
    const watchCodigoPostal = useWatch({control, name: `${nameBase}.${ClientAddressFields.PostalCode}`});
    const watchProvincia = useWatch({control, name: `${nameBase}.${ClientAddressFields.ProvinceCode}`});
    const watchPartido = useWatch({control, name: `${nameBase}.${ClientAddressFields.DepartmentCode}`});
    

    const loadAddressData = useCallback(async (CP: string) => {
        if (CP) {
            const data = await HttpClientAddressData.getAddressDataByPostalCodeAndCountry(1, CP);
            const unique_provinces = removeDuplicatesById(data.lstProvincias) as Province[];
            const unique_partidos = removeDuplicatesById(data.lstPartidos) as Department[];
            const unique_localities = removeDuplicatesById(data.lstLocalidades) as Municipality[];
            setValue(nameBase, { ...getValues(nameBase),
                [ClientAddressFields.ProvinceCode]: 0,
                [ClientAddressFields.DepartmentCode]: null,
                [ClientAddressFields.MunicipalityCode]: null,
            })
            setProvinces(unique_provinces);
            setPartidos(unique_partidos);
            setLocalities(unique_localities);
        }
    }, [watchPais, watchCodigoPostal]);

    const loadFilteredPartidos = () => {
        setFilteredPartidos(partidos?.filter(p => p.intCodProvincia === watchProvincia) || null)
    }

    const loadFilteredLocalities = () => {
        if (watchPartido) {
            setFilteredLocalities(localities?.filter(l => l.intCodProvincia === watchProvincia
                && l.intCodPartido === watchPartido) || null)
        } else {
            setFilteredLocalities(localities?.filter(l => l.intCodProvincia === watchProvincia) || null)
        }
    }

    useEffect(() => {
        loadFilteredPartidos();
    }, [partidos, watchProvincia])

    useEffect(() => {
        loadFilteredLocalities();
    }, [localities, watchPartido, watchProvincia])

    useEffect(() => {
        if (provinces?.length === 1) {
            setValue(`${nameBase}.${ClientAddressFields.ProvinceCode}`, provinces[0].id)
        }
    }, [provinces])

    useEffect(() => {
        if (filteredPartidos?.length === 1) {
            setValue(`${nameBase}.${ClientAddressFields.DepartmentCode}`, filteredPartidos[0].id)
        }
    }, [filteredPartidos])

    useEffect(() => {
        if (filteredlocalities?.length === 1) {
            setValue(`${nameBase}.${ClientAddressFields.MunicipalityCode}`, filteredlocalities[0].id)
        }
    }, [filteredlocalities])

    useEffect(() => {
        (async () => {
            if (watchPais !== null) {
                await loadAddressData(watchCodigoPostal || '');
            }
        })();
    }, [watchPais])

    const onBlurCP = useCallback(() => {
        if (watchPais !== null) {
            loadAddressData(watchCodigoPostal || '');
        }

    },[watchPais, watchCodigoPostal])

    return (
        <Grid container justifyContent={'center'} spacing={4}>
            <Grid item xs={7.5} justifyContent={'center'}>
                <ControlledTextField
                    name={`${nameBase}.${ClientAddressFields.Street}`}
                    label='Calle'
                    control={control}
                    fullWidth
                />
            </Grid>
            <Grid item xs={2.5}>
                <ControlledTextField
                    name={`${nameBase}.${ClientAddressFields.StreetNumber}`}
                    label='Número'
                    control={control}
                    fullWidth
                />
            </Grid>
            <Grid item xs={2}>
                <ControlledTextField
                    name={`${nameBase}.${ClientAddressFields.Floor}`}
                    label='Piso'
                    control={control}
                    fullWidth
                />
            </Grid>
            <Grid item xs={4}>
                <ControlledTextField control={control}
                                     name={`${nameBase}.${ClientAddressFields.Apartment}`}
                                     label='Departamento'
                                     fullWidth
                />
            </Grid>
            <Grid item xs={4}>
                <ControlledTextField control={control}
                                     onBlur={onBlurCP}
                                     name={`${nameBase}.${ClientAddressFields.PostalCode}`}
                                     label='Cód. Postal'
                                     fullWidth
                />
            </Grid>
            <Grid item xs={4}>
                <ControlledTextField select
                                     options={provinces || []}
                                     control={control}
                                     name={`${nameBase}.${ClientAddressFields.ProvinceCode}`}
                                     label='Provincia'
                                     disabled
                                     fullWidth
                />
            </Grid>
            <Grid item xs={6}>
                <ControlledTextField select
                                     options={filteredPartidos || []}
                                     disabled={!filteredPartidos || filteredPartidos.length <= 1}
                                     control={control}
                                     name={`${nameBase}.${ClientAddressFields.DepartmentCode}`}
                                     label='Departmento'
                                     fullWidth
                />
            </Grid>
            <Grid item xs={6}>
                <ControlledTextField select
                                     options={filteredlocalities || []}
                                     disabled={!filteredlocalities || filteredlocalities.length <= 1}
                                     control={control}
                                     name={`${nameBase}.${ClientAddressFields.MunicipalityCode}`}
                                     label='Localidad'
                                     fullWidth
                />
            </Grid>
        </Grid>
    )
}


export default AddressFormContentData